import React from "react";
import { ModuleI, LessonI, BottomNavigationBarType } from "../../../types";

interface LessonSideNavProps {
  module: ModuleI;
  onModuleNavigate: () => void | Promise<void>;
  currentLesson: LessonI;
  currentPage: number;
  onPageSelect: (pageIndex: number) => void;
  onLessonSelect: (lesson: LessonI, pageIndex: number) => void;
  recapPageIndex: number;
}

const LessonSideNav: React.FC<LessonSideNavProps> = ({
  module,
  onModuleNavigate,
  currentLesson,
  currentPage,
  onPageSelect,
  onLessonSelect,
  recapPageIndex,
}) => {
  const isActivePage = (lesson: LessonI, pageIndex: number) =>
    lesson._id === currentLesson._id && pageIndex === currentPage;

  return (
    <nav
      aria-label="Lesson navigation"
      className="hidden lg:flex flex-col lg:w-80 shrink-0 bg-white rounded-xl border border-gray-200 mb-24 overflow-hidden"
    >
      {/* Module title */}
      <button
        type="button"
        onClick={() => void onModuleNavigate()}
        className="group w-full text-left px-4 py-2 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-electrum focus-visible:ring-offset-1 cursor-pointer"
        aria-label={`Open module overview: ${module.title}`}
      >
        <span className="text-xs font-bold tracking-widest uppercase text-electrum group-hover:underline decoration-electrum underline-offset-2">
          {module.title}
        </span>
      </button>

      <ul className="flex flex-col gap-0">
        {module.lessons.map((lesson, lessonIndex) => {
          const isCurrentLesson = lesson._id === currentLesson._id;

          return (
            <li key={lesson._id ?? lesson.title}>
              {/* Lesson header */}
              <div
                className={`w-full flex items-center gap-3 px-4 py-3 ${
                  isCurrentLesson
                    ? "bg-electrum-50 text-electrum font-semibold"
                    : "text-gray-600"
                }`}
              >
                {lesson.icon?.src ? (
                  <img
                    src={lesson.icon.src}
                    alt={lesson.icon.alt ?? lesson.title}
                    className="h-10 w-10 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-electrum-100 shrink-0" />
                )}
                <span
                  className={`flex-1 text-sm font-semibold leading-snug ${
                    isCurrentLesson ? "text-electrum" : "text-gray-800"
                  }`}
                >
                  {lesson.title}
                </span>
              </div>

              {/* Page list — always visible */}
              <ul className="pb-2">
                {lesson.pages.map((page, pageIndex) => {
                  const isActive = isActivePage(lesson, pageIndex);

                  return (
                    <li key={page._id ?? pageIndex}>
                      <button
                        type="button"
                        onClick={() => {
                          if (isCurrentLesson) {
                            onPageSelect(pageIndex);
                          } else {
                            onLessonSelect(lesson, pageIndex);
                          }
                        }}
                        className={`w-full flex items-center gap-3 pl-6 pr-4 py-2 text-left text-sm transition-colors cursor-pointer ${
                          isActive
                            ? "bg-electrum-50 text-electrum font-semibold"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        }`}
                      >
                        <span className="shrink-0 flex items-center justify-center h-4 w-4">
                          {isActive ? (
                            <span className="h-2.5 w-2.5 rounded-full bg-electrum block" />
                          ) : (
                            <span className="h-2 w-2 rounded-full border border-gray-300 block" />
                          )}
                        </span>
                        <span className="leading-snug">{page.title}</span>
                      </button>
                    </li>
                  );
                })}
                {lesson.bottomNavigationBarType !==
                  BottomNavigationBarType.SINGLE_PAGE && (
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        if (isCurrentLesson) {
                          onPageSelect(lesson.pages.length);
                        } else {
                          onLessonSelect(lesson, lesson.pages.length);
                        }
                      }}
                      className={`w-full flex items-center gap-3 pl-6 pr-4 py-2 text-left text-sm transition-colors cursor-pointer ${
                        isCurrentLesson && currentPage === recapPageIndex
                          ? "bg-electrum-50 text-electrum font-semibold"
                          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                      }`}
                    >
                      <span className="shrink-0 flex items-center justify-center h-4 w-4">
                        {isCurrentLesson && currentPage === recapPageIndex ? (
                          <span className="h-2.5 w-2.5 rounded-full bg-electrum block" />
                        ) : (
                          <span className="h-2 w-2 rounded-full border border-gray-300 block" />
                        )}
                      </span>
                      <span className="leading-snug">Recap</span>
                    </button>
                  </li>
                )}
              </ul>

              {lessonIndex < module.lessons.length - 1 && (
                <div className="mx-0 border-b border-gray-100" />
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default LessonSideNav;
